"use client";

import React from "react";
import { ArrowRight, Clock, MapPin, Newspaper } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function CityWireGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(7)].map((_, i) => <div key={i} data-index={i} className={`flex flex-col gap-4 ${i === 0 ? "md:col-span-2 lg:col-span-2" : ""}`}>
            <div className={`w-full animate-pulse rounded-xl bg-muted/50 ${i === 0 ? "h-64 md:h-96" : "h-48"}`} />
            <div className="h-6 w-24 animate-pulse rounded-md bg-muted/50" />
            <div className="h-8 w-full animate-pulse rounded-md bg-muted/50" />
            <div className="h-16 w-full animate-pulse rounded-md bg-muted/50" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <Newspaper className="mb-4 size-12 text-muted-foreground" />
        <h3 className="text-xl font-semibold text-foreground">Unable to load the wire</h3>
        <p className="mt-2 text-sm text-muted-foreground">Please check back later for the latest updates.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <Newspaper className="mb-4 size-12 text-muted-foreground" />
        <h3 className="text-xl font-semibold text-foreground">The Wire is Quiet</h3>
        <p className="mt-2 text-sm text-muted-foreground">No news to report at this time.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
      {posts.map((post, i) => {
      const isFeatured = i === 0;
      const imageUrl = post.featuredImage || `/img/generic_news_placeholder_${i % 3 + 1}.png`;
      const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "News";
      return <article key={post.id} data-index={i} className={`group flex flex-col gap-4 ${isFeatured ? "md:col-span-2 lg:col-span-2" : "col-span-1"}`}>
            <Link to={post.link || "#"} className="block overflow-hidden rounded-xl">
              <div className={`relative w-full overflow-hidden bg-muted ${isFeatured ? "aspect-video md:aspect-[16/9]" : "aspect-[4/3]"}`}>
                <img src={imageUrl} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading={isFeatured ? "eager" : "lazy"} />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-60 transition-opacity group-hover:opacity-80" />
              </div>
            </Link>

            <div className="flex flex-col items-start gap-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-[var(--chart-2)] text-white hover:bg-[var(--chart-2)]/90 border-none uppercase tracking-wider text-[10px] font-bold">
                  {categoryName}
                </Badge>
                {post.date && <span className="flex items-center text-xs text-muted-foreground">
                    <Clock className="mr-1 size-3" />
                    {new Date(post.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
                  </span>}
              </div>

              <Link to={post.link || "#"} className="block group-hover:text-primary transition-colors">
                <h3 className={`font-serif uppercase leading-tight ${isFeatured ? "text-3xl md:text-4xl" : "text-2xl"}`}>
                  {post.title}
                </h3>
              </Link>

              <p className="text-sm text-muted-foreground line-clamp-3">
                {post.excerpt ? post.excerpt.replace(/(<([^>]+)>)/gi, "") : "Read the full story to get the latest updates and insights from the Elbo Room network."}
              </p>

              {isFeatured && <Link to={post.link || "#"} className="mt-2 inline-flex items-center text-sm font-bold text-primary hover:text-primary/80 uppercase tracking-wider">
                  Read Full Story <ArrowRight className="ml-2 size-4" />
                </Link>}
            </div>
          </article>;
    })}
    </div>;
}
export default function TheCityWire() {
  const wp_query_params = {
    per_page: 7,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-city-wire" className="dark bg-background text-foreground relative w-full overflow-hidden py-16 md:py-24">
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full" />

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 border-b border-border/50 pb-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-3 flex items-center gap-2 text-primary">
              <MapPin className="size-5" />
              <span className="text-sm font-bold uppercase tracking-widest">Fort Lauderdale, FL</span>
            </div>
            <h2 className="font-serif text-5xl uppercase tracking-tight md:text-6xl lg:text-7xl">
              The City Wire
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Your definitive information hub for local travel tips, city events, real estate news, and Elbo Room corporate updates.
            </p>
          </div>
          
          <Link to="/ElboRoomHubPage">
            <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground">
              View All News <ArrowRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider className="city-wire-posts-wrapper" wp_query={{
        per_page: 7,
        order: "desc",
        orderby: "date"
      }}>
          <CityWireGrid />
        </PostsProvider>
      </div>
    </section>;
}